var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// src/services/images/ImageLoading.ts
import * as THREE from 'three';
const DEFAULT_SIZE = 256;
const DEFAULT_RADIUS = Math.round(DEFAULT_SIZE * (28 / 192)); // ≈ 37
// --- defaults ---
const defaultOpts = {
    size: DEFAULT_SIZE,
    radius: DEFAULT_RADIUS,
    maskStyle: 'circle',
    svgColor: 'rgb(240,240,240)',
    scale: 140,
    forceSvgDefaultWhite: true,
    originalUrl: '',
    preferDataUrlForSvg: true,
};
// --- caches / locks ---
const inflight = new Map();
const spriteCache = new Map();
const textureCache = new Map();
/** Public: attach sprite to any Object3D and return it */
export function attachMaskedSprite(parent, url, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const sprite = yield createMaskedSprite(url, options);
        parent.add(sprite);
        return sprite;
    });
}
/** Public: create a masked sprite from URL */
export function createMaskedSprite(url, opts) {
    return __awaiter(this, void 0, void 0, function* () {
        const merged = Object.assign(Object.assign({}, defaultOpts), (opts || {}));
        const svgColorCss = toCssColor(merged.svgColor);
        const o = Object.assign(Object.assign({}, merged), { svgColor: svgColorCss });
        const key = buildKey(url, o);
        vlog(o, 'createMaskedSprite:begin', { url, originalUrl: o.originalUrl, key });
        // cache hit?
        const cached = spriteCache.get(key);
        if (cached) {
            vlog(o, 'createMaskedSprite:cache-hit:sprite', { key });
            return cached;
        }
        // inflight reuse?
        let p = inflight.get(key);
        if (p) {
            vlog(o, 'createMaskedSprite:inflight-reuse', { key });
            return p;
        }
        p = (() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const loaded = yield loadImageSmart(url, o);
                vlog(o, 'createMaskedSprite:loaded', {
                    path: loaded.debug,
                    loadedAsSvg: loaded.loadedAsSvg,
                    viewBox: loaded.viewBox,
                    // naturalW/H only present on <img>; ImageBitmap has width/height
                    width: sourceWidth(loaded.img),
                    height: sourceHeight(loaded.img),
                });
                const canvas = drawMaskedCanvas(loaded, o);
                const tex = (_a = textureCache.get(key)) !== null && _a !== void 0 ? _a : new THREE.CanvasTexture(canvas);
                if (!textureCache.has(key)) {
                    tex.anisotropy = 4;
                    tex.generateMipmaps = true;
                    tex.needsUpdate = true;
                    textureCache.set(key, tex);
                    vlog(o, 'createMaskedSprite:texture:created', { key, canvas: { w: canvas.width, h: canvas.height } });
                }
                else {
                    tex.image = canvas;
                    tex.needsUpdate = true;
                    vlog(o, 'createMaskedSprite:texture:updated', { key });
                }
                const mat = new THREE.SpriteMaterial({
                    map: tex,
                    transparent: true,
                    color: new THREE.Color(o.svgColor) // <- tint multiply
                });
                const sprite = new THREE.Sprite(mat);
                sprite.scale.setScalar(o.scale);
                if (opts === null || opts === void 0 ? void 0 : opts.position)
                    sprite.position.copy(opts.position);
                spriteCache.set(key, sprite);
                vlog(o, 'createMaskedSprite:success', { key });
                return sprite;
            }
            catch (err) {
                verr(o, 'createMaskedSprite:error', err);
                const fallback = makeErrorSprite(o);
                spriteCache.set(key, fallback);
                return fallback;
            }
            finally {
                inflight.delete(key);
            }
        }))();
        inflight.set(key, p);
        return p;
    });
}
function loadImageSmart(url, o) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        vlog(o, 'loadImageSmart:fetch:begin', { url, originalUrl: o.originalUrl });
        let res;
        try {
            res = yield fetch(url, { mode: 'cors', credentials: 'omit' });
        }
        catch (e) {
            verr(o, 'loadImageSmart:fetch:network-error', { error: e, url });
            throw e;
        }
        const headers = headersToRecord(res.headers);
        const contentType = (res.headers.get('content-type') || '').toLowerCase();
        vlog(o, 'loadImageSmart:fetch:status', { status: res.status, ok: res.ok, contentType, headers });
        if (!res.ok) {
            const bodySnippet = yield safeReadText(res); // uses res.clone() 👍
            verr(o, 'loadImageSmart:fetch:not-ok', { status: res.status, bodySnippet: bodySnippet.slice(0, 600) });
            throw new Error(`HTTP ${res.status} for ${url}`);
        }
        // --- Read the body exactly once ---
        let buf;
        try {
            buf = yield res.arrayBuffer();
        }
        catch (e) {
            verr(o, 'loadImageSmart:arrayBuffer:error', e);
            throw e;
        }
        const looksSvgByUrl = /\.svg(\?|$)/i.test(url) || /\.svg(\?|$)/i.test(o.originalUrl || '');
        const maybeSvgByType = contentType.includes('image/svg+xml') || contentType.includes('svg');
        // Try to interpret as SVG if headers/URL suggest SVG
        let asText;
        let isSvg = false;
        if (looksSvgByUrl || maybeSvgByType) {
            try {
                asText = new TextDecoder('utf-8', { fatal: false }).decode(buf);
                isSvg = looksLikeSvg(asText);
                vlog(o, 'loadImageSmart:text:decoded', { length: asText === null || asText === void 0 ? void 0 : asText.length, isSvg });
            }
            catch (e) {
                verr(o, 'loadImageSmart:text:decode-error', e);
            }
        }
        const original = o.originalUrl || url;
        const isTabler = isLikelyTablerUrl(original);
        if (isSvg && asText) {
            vlog(o, 'loadImageSmart:svg:detected', { isTabler, url, original });
            let sanitized = '';
            try {
                sanitized = sanitizeSvg(asText);
            }
            catch (e) {
                verr(o, 'loadImageSmart:svg:sanitize-error', e);
                sanitized = asText;
            }
            let normalized = sanitized;
            try {
                if (isTabler) {
                    normalized = normalizeTablerSvgColor(sanitized, o.svgColor);
                }
                else {
                    normalized = normalizeGenericSvg(sanitized, { forceWhite: o.forceSvgDefaultWhite });
                }
            }
            catch (e) {
                verr(o, 'loadImageSmart:svg:normalize-error', e);
                normalized = sanitized;
            }
            const vb = (_a = sniffViewBox(normalized)) !== null && _a !== void 0 ? _a : (isTabler ? { x: 0, y: 0, w: 24, h: 24 } : undefined);
            // ✅ Try ImageBitmap path first — no <img>, no CSP img-src needed
            try {
                const { img } = yield bitmapFromSvgString(normalized, 'svg-bitmap-first');
                return { img, loadedAsSvg: true, viewBox: vb, debug: 'svg-bitmap' };
            }
            catch (eBitmap) {
                verr(o, 'loadImageSmart:svg:bitmap-error', eBitmap);
            }
            // Fallbacks that use <img> (may be blocked by CSP)
            try {
                const { img } = yield imageFromSvgString(normalized, o, 'svg-data-first');
                return { img, loadedAsSvg: true, viewBox: vb, debug: 'svg-data' };
            }
            catch (eData) {
                verr(o, 'loadImageSmart:svg:data-url-error', eData);
                try {
                    const { img } = yield imageFromSvgBlob(normalized, o, 'svg-blob-fallback');
                    return { img, loadedAsSvg: true, viewBox: vb, debug: 'svg-blob' };
                }
                catch (eBlob) {
                    verr(o, 'loadImageSmart:svg:blob-url-error', eBlob);
                    // fall through to raster attempt with the same buffer (rare)
                }
            }
        }
        // Raster/unknown: build Blob from the same buffer
        const type = contentType || 'application/octet-stream';
        const blob = new Blob([buf], { type });
        // ✅ Try ImageBitmap first
        try {
            const { img } = yield bitmapFromBlob(blob, 'raster-bitmap');
            return { img, loadedAsSvg: false, debug: 'raster-bitmap' };
        }
        catch (eBitmap) {
            verr(o, 'loadImageSmart:raster:bitmap-error', eBitmap);
        }
        // Fallback to <img> if bitmap fails
        const { img } = yield imageFromBlob(blob, o, 'raster-direct');
        return { img, loadedAsSvg: false, debug: 'raster-direct' };
    });
}
function looksLikeSvg(s) {
    const t = s.trim().slice(0, 2048).toLowerCase();
    return t.includes('<svg');
}
function isLikelyTablerUrl(u) {
    try {
        const lu = u.toLowerCase();
        return lu.includes('tabler');
    }
    catch (_a) {
        return /tabler/i.test(u);
    }
}
function sniffViewBox(svg) {
    const m = svg.match(/viewBox\s*=\s*["']\s*([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s*["']/i);
    if (!m)
        return;
    const [x, y, w, h] = m.slice(1).map(parseFloat);
    if (!isFinite(x) || !isFinite(y) || !isFinite(w) || !isFinite(h) || w <= 0 || h <= 0)
        return;
    return { x, y, w, h };
}
/**
 * Robust Tabler recolor: keep stroke="currentColor" semantics,
 * set root color and style so all currentColor-based strokes adopt it,
 * normalize basic attributes, and gently fix obvious fills/strokes.
 */
function normalizeTablerSvgColor(src, _colorCss) {
    var _a;
    const open = (_a = src.match(/<svg\b[^>]*>/i)) === null || _a === void 0 ? void 0 : _a[0];
    if (!open)
        return src;
    const end = src.lastIndexOf('</svg>');
    const bodyRaw = end >= 0 ? src.slice(open.length, end) : src.slice(open.length);
    let svgTag = open;
    // Ensure viewBox; strip width/height; preserve aspect ratio
    if (!/viewBox\s*=/.test(svgTag))
        svgTag = svgTag.replace(/<svg\b/i, `<svg viewBox="0 0 24 24"`);
    svgTag = svgTag
        .replace(/\bwidth\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\bheight\s*=\s*["'][^"']*["']/gi, '');
    if (!/\bpreserveAspectRatio\b/.test(svgTag)) {
        svgTag = svgTag.replace(/<svg\b/i, `<svg preserveAspectRatio="xMidYMid meet"`);
    }
    // remove any root color/currentColor semantics
    svgTag = svgTag
        .replace(/\bcolor\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\bstyle\s*=\s*["']([^"']*)["']/gi, (m, style) => {
        const pruned = style.split(';').map((s) => s.trim()).filter((s) => s && !/^color\s*:/i.test(s)).join(';');
        return pruned ? ` style="${pruned}"` : '';
    });
    // Whiten all primitives: stroke white, fill none; keep existing stroke-width if present
    const body = bodyRaw.replace(/<(path|circle|rect|line|polyline|polygon|ellipse)\b([^>]*)>/gi, (m, tag, attrs) => {
        let a = attrs
            .replace(/\bstroke\s*=\s*["'][^"']*["']/gi, '') // drop any stroke
            .replace(/\bfill\s*=\s*["'][^"']*["']/gi, ''); // drop any fill
        // Keep stroke-width if already present, else default 2 (Tabler default)
        const hasStrokeWidth = /\bstroke-width\s*=\s*["'][^"']*["']/i.test(attrs);
        if (!hasStrokeWidth)
            a += ` stroke-width="2"`;
        // force white stroke and no fill
        a += ` stroke="#ffffff" fill="none"`;
        return `<${tag}${a}>`;
    });
    return `${svgTag}${body}</svg>`;
}
/**
 * Generic SVG normalization. Leave colors as-is, but ensure sane sizing.
 */
function normalizeGenericSvg(src, opts) {
    var _a, _b, _c, _d, _e;
    const open = (_a = src.match(/<svg\b[^>]*>/i)) === null || _a === void 0 ? void 0 : _a[0];
    if (!open)
        return src;
    let svgTag = open;
    let body = src.slice(open.length, src.lastIndexOf('</svg>'));
    if (!/viewBox\s*=/.test(svgTag)) {
        const w = parseFloat((_c = (_b = open.match(/\bwidth\s*=\s*["']\s*([-\d.]+)/i)) === null || _b === void 0 ? void 0 : _b[1]) !== null && _c !== void 0 ? _c : '24');
        const h = parseFloat((_e = (_d = open.match(/\bheight\s*=\s*["']\s*([-\d.]+)/i)) === null || _d === void 0 ? void 0 : _d[1]) !== null && _e !== void 0 ? _e : '24');
        svgTag = svgTag.replace(/<svg\b/i, `<svg viewBox="0 0 ${isFinite(w) ? w : 24} ${isFinite(h) ? h : 24}"`);
    }
    svgTag = svgTag
        .replace(/\bwidth\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\bheight\s*=\s*["'][^"']*["']/gi, '');
    if (!/\bpreserveAspectRatio\b/.test(svgTag)) {
        svgTag = svgTag.replace(/<svg\b/i, `<svg preserveAspectRatio="xMidYMid meet"`);
    }
    if (opts.forceWhite) {
        body = body.replace(/<(path|circle|rect|line|polyline|polygon|ellipse)\b([^>]*)>/gi, (m, tag, attrs) => {
            let a = attrs
                .replace(/\bstroke\s*=\s*["'][^"']*["']/gi, '')
                .replace(/\bfill\s*=\s*["'][^"']*["']/gi, '');
            const hasStrokeWidth = /\bstroke-width\s*=\s*["'][^"']*["']/i.test(attrs);
            if (!hasStrokeWidth)
                a += ` stroke-width="2"`;
            a += ` stroke="#ffffff" fill="none"`;
            return `<${tag}${a}>`;
        });
    }
    return `${svgTag}${body}</svg>`;
}
function sanitizeSvg(src) {
    return src
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/\b(xlink:href|href)\s*=\s*["']\s*javascript:[^"']*["']/gi, '');
}
/* --------------------------
 * Image creation helpers
 * -------------------------*/
// ✅ Prefer ImageBitmap paths (no <img>, CSP-friendly)
// Render SVG text to ImageBitmap without <img>
function bitmapFromSvgString(svg, tag) {
    return __awaiter(this, void 0, void 0, function* () {
        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
        // Some browsers render alpha better with premultiply
        const img = yield createImageBitmap(blob, { premultiplyAlpha: 'premultiply' }).catch(() => createImageBitmap(blob));
        return { img };
    });
}
// Render raster Blob (PNG/JPEG/etc.) to ImageBitmap without <img>
function bitmapFromBlob(blob, tag) {
    return __awaiter(this, void 0, void 0, function* () {
        const img = yield createImageBitmap(blob, { premultiplyAlpha: 'premultiply' }).catch(() => createImageBitmap(blob));
        return { img };
    });
}
// --- Fallbacks that use <img> (may be blocked by CSP) ---
// Prefer data URL first (safe for CSPs that disallow blob:), then fallback to blob if needed.
function imageFromSvgString(svg, o, tag) {
    return __awaiter(this, void 0, void 0, function* () {
        if (o.preferDataUrlForSvg) {
            const dataUrl = svgToDataUrl(svg);
            vlog(o, `imageFromSvgString:${tag}:using-data-url`, { len: dataUrl.length });
            try {
                const img = yield loadHtmlImage(dataUrl, true, o, `${tag}:data`);
                return { img };
            }
            catch (e) {
                verr(o, `imageFromSvgString:${tag}:data-url-onerror`, e);
                // fall through to blob
            }
        }
        return imageFromSvgBlob(svg, o, tag);
    });
}
function imageFromSvgBlob(svg, o, tag) {
    return __awaiter(this, void 0, void 0, function* () {
        const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        vlog(o, `imageFromSvgBlob:${tag}:objectURL-created`, { size: blob.size });
        try {
            const img = yield loadHtmlImage(blobUrl, true, o, `${tag}:blob`);
            return { img };
        }
        finally {
            try {
                URL.revokeObjectURL(blobUrl);
            }
            catch (_a) { }
            vlog(o, `imageFromSvgBlob:${tag}:objectURL-revoked`);
        }
    });
}
function imageFromBlob(blob, o, tag) {
    return __awaiter(this, void 0, void 0, function* () {
        const objUrl = URL.createObjectURL(blob);
        vlog(o, `imageFromBlob:${tag}:objectURL-created`, { size: blob.size, type: blob.type });
        try {
            const img = yield loadHtmlImage(objUrl, false, o, tag);
            return { img };
        }
        finally {
            try {
                URL.revokeObjectURL(objUrl);
            }
            catch (_a) { }
            vlog(o, `imageFromBlob:${tag}:objectURL-revoked`);
        }
    });
}
function loadHtmlImage(src, decodePreferred, o, tag) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img._debugSrc = src;
        img.crossOrigin = 'anonymous';
        const done = (ok, info) => {
            if (ok)
                resolve(img);
            else
                reject(new Error(`[ImageLoading] img.onerror tag=${tag} src=${src} info=${JSON.stringify(info)}`));
        };
        img.onload = () => __awaiter(this, void 0, void 0, function* () {
            try {
                const info = {
                    tag,
                    src,
                    naturalWidth: img.naturalWidth,
                    naturalHeight: img.naturalHeight,
                    width: img.width,
                    height: img.height,
                    devicePixelRatio: (typeof window !== 'undefined' ? window.devicePixelRatio : undefined),
                };
                vlog(o, 'img.onload', info);
                if (decodePreferred && img.decode) {
                    try {
                        yield img.decode();
                        vlog(o, 'img.decode:ok', { tag });
                    }
                    catch (e) {
                        verr(o, 'img.decode:error', e);
                    }
                }
                done(true, info);
            }
            catch (e) {
                verr(o, 'img.onload:postprocess-error', e);
                done(false, { tag, error: String(e) });
            }
        });
        img.onerror = (evt) => {
            const errInfo = { tag, src, type: evt === null || evt === void 0 ? void 0 : evt.type, message: 'Image element onerror fired (possible CSP/CORS or URL revoked too early)' };
            verr(o, 'img.onerror', errInfo);
            done(false, errInfo);
        };
        try {
            img.src = src;
            vlog(o, 'img.src:set', { tag, src });
        }
        catch (e) {
            verr(o, 'img.src:set-error', { tag, src, error: e });
            reject(e);
        }
    });
}
/* --------------------------
 * Canvas & Masking
 * -------------------------*/
function drawMaskedCanvas(loaded, o) {
    var _a, _b;
    const size = o.size;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, size, size);
    if (o.maskStyle !== 'none') {
        ctx.save();
        ctx.beginPath();
        if (o.maskStyle === 'circle') {
            // Match legacy behavior: circle exactly fills the canvas so the
            // drawn image can fully occupy the mask area.
            const r = size / 2;
            ctx.arc(r, r, r, 0, Math.PI * 2);
        }
        else if (o.maskStyle === 'roundedSquare') {
            roundedRectPath(ctx, 0, 0, size, size, o.radius);
        }
        else {
            ctx.rect(0, 0, size, size);
        }
        ctx.clip();
    }
    // For circles, let the image fill the full mask; for other shapes,
    // keep a small inset so corners don’t butt right up against the edge.
    const pad = (o.maskStyle === 'circle')
        ? 0
        : Math.round(size * 0.02);
    const targetW = size - pad * 2;
    const targetH = size - pad * 2;
    const vbW = (_a = loaded.viewBox) === null || _a === void 0 ? void 0 : _a.w;
    const vbH = (_b = loaded.viewBox) === null || _b === void 0 ? void 0 : _b.h;
    const srcW = (vbW && vbW > 0) ? vbW : sourceWidth(loaded.img);
    const srcH = (vbH && vbH > 0) ? vbH : sourceHeight(loaded.img);
    const srcAspect = srcW / srcH;
    let drawW, drawH;
    if (srcAspect >= 1) {
        drawW = targetW;
        drawH = Math.round(targetW / srcAspect);
        if (drawH > targetH) {
            drawH = targetH;
            drawW = Math.round(targetH * srcAspect);
        }
    }
    else {
        drawH = targetH;
        drawW = Math.round(targetH * srcAspect);
        if (drawW > targetW) {
            drawW = targetW;
            drawH = Math.round(targetW / srcAspect);
        }
    }
    const dx = Math.round((size - drawW) / 2);
    const dy = Math.round((size - drawH) / 2);
    vlog(o, 'drawMaskedCanvas:draw', {
        size, pad, targetW, targetH,
        srcW, srcH, srcAspect, drawW, drawH, dx, dy,
        loadedDebug: loaded.debug,
    });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(loaded.img, dx, dy, drawW, drawH);
    if (o.maskStyle !== 'none')
        ctx.restore();
    return canvas;
}
function roundedRectPath(ctx, x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
}
/* --------------------------
 * Helpers / Cache control
 * -------------------------*/
export function getUrlValueAtPath(obj, path) {
    return path.split('.').reduce((acc, part) => acc === null || acc === void 0 ? void 0 : acc[part], obj);
}
function buildKey(url, o) {
    const idUrl = url || o.originalUrl || '';
    return `${idUrl}::orig=${o.originalUrl}::${o.size}::${o.radius}::${o.maskStyle}::${o.svgColor}::fw=${o.forceSvgDefaultWhite ? 1 : 0}`;
}
export function releaseMaskedTexture(urlPrefix) {
    var _a, _b;
    let disposed = 0;
    const match = (key) => !urlPrefix || key.startsWith(urlPrefix) || key.includes(`${urlPrefix}::`);
    for (const [key, tex] of textureCache) {
        if (!match(key))
            continue;
        try {
            tex.dispose();
        }
        catch (_c) { }
        textureCache.delete(key);
        disposed++;
    }
    for (const [key, sprite] of spriteCache) {
        if (!match(key))
            continue;
        try {
            const mat = sprite.material;
            if (mat) {
                if (mat.map) {
                    try {
                        (_b = (_a = mat.map).dispose) === null || _b === void 0 ? void 0 : _b.call(_a);
                    }
                    catch (_d) { }
                    mat.map = null;
                }
                try {
                    mat.dispose();
                }
                catch (_e) { }
            }
        }
        catch (_f) { }
        spriteCache.delete(key);
    }
    for (const key of Array.from(inflight.keys())) {
        if (match(key))
            inflight.delete(key);
    }
    return disposed;
}
export function releaseAllMaskedTextures() {
    return releaseMaskedTexture(undefined);
}
function makeErrorSprite(o) {
    const c = document.createElement('canvas');
    c.width = o.size;
    c.height = o.size;
    const g = c.getContext('2d');
    g.fillStyle = 'rgba(20,20,20,0.9)';
    g.fillRect(0, 0, o.size, o.size);
    g.strokeStyle = '#e74c3c';
    g.lineWidth = 10;
    roundedRectPath(g, 5, 5, o.size - 10, o.size - 10, o.radius);
    g.stroke();
    g.fillStyle = '#e74c3c';
    g.font = `bold ${Math.round(o.size * 0.2)}px sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('IMG', o.size / 2, o.size / 2);
    const tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
    const s = new THREE.Sprite(mat);
    s.scale.setScalar(o.scale);
    return s;
}
/* --------------------------
 * Logging / Color / Util
 * -------------------------*/
function vlog(opts, msg, data) {
    if (opts === null || opts === void 0 ? void 0 : opts.debugLabel) {
        try {
            console.log(`[ImageLoading] ${opts.debugLabel} :: ${msg}`);
            if (data !== undefined)
                console.dir(data, { depth: 6 });
        }
        catch (_a) { }
    }
}
function verr(opts, msg, data) {
    if (opts === null || opts === void 0 ? void 0 : opts.debugLabel) {
        try {
            console.error(`[ImageLoading] ${opts.debugLabel} :: ${msg}`);
            if (data !== undefined)
                console.dir(data, { depth: 6 });
        }
        catch (_a) { }
    }
    else {
        console.error('[ImageLoading]', msg, data);
    }
}
function headersToRecord(h) {
    const out = {};
    h.forEach((value, key) => { out[key] = value; });
    return out;
}
function safeReadText(res) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            return yield res.clone().text();
        }
        catch (_a) {
            return '';
        }
    });
}
// color normalization
function toCssColor(input) {
    if (input == null)
        return 'rgb(240,240,240)';
    if (typeof input === 'number') {
        const r = (input >> 16) & 0xff;
        const g = (input >> 8) & 0xff;
        const b = input & 0xff;
        return `rgb(${r},${g},${b})`;
    }
    const s = String(input).trim();
    if (/^0x[0-9a-f]{6}$/i.test(s)) {
        const n = parseInt(s.slice(2), 16);
        const r = (n >> 16) & 0xff;
        const g = (n >> 8) & 0xff;
        const b = n & 0xff;
        return `rgb(${r},${g},${b})`;
    }
    if (/^#[0-9a-f]{6}$/i.test(s)) {
        const n = parseInt(s.slice(1), 16);
        const r = (n >> 16) & 0xff;
        const g = (n >> 8) & 0xff;
        const b = n & 0xff;
        return `rgb(${r},${g},${b})`;
    }
    if (/^rgb\(/i.test(s) || /^hsl\(/i.test(s))
        return s;
    return s;
}
// safe SVG → data URL (used by <img> fallback)
function svgToDataUrl(svg) {
    // encode minimally but safely for unicode; avoid base64 so devtools stays readable
    const encoded = encodeURIComponent(svg)
        .replace(/'/g, '%27')
        .replace(/"/g, '%22');
    return `data:image/svg+xml;charset=utf-8,${encoded}`;
}
// helpers for ImageBitmap/HTMLImageElement interop
function sourceWidth(src) {
    var _a, _b, _c;
    return (_c = (_b = (_a = src.width) !== null && _a !== void 0 ? _a : src.naturalWidth) !== null && _b !== void 0 ? _b : src.width) !== null && _c !== void 0 ? _c : 1;
}
function sourceHeight(src) {
    var _a, _b, _c;
    return (_c = (_b = (_a = src.height) !== null && _a !== void 0 ? _a : src.naturalHeight) !== null && _b !== void 0 ? _b : src.height) !== null && _c !== void 0 ? _c : 1;
}
